import React, { createContext, useContext, useCallback, useMemo, useEffect } from 'react';
import { auth } from '../better-auth';
import type {
    AuthContextType,
    RBACUser,
    RBACSession,
    RoleName,
    Permission
} from '../types/rbac';
import {
    sanitizeUserRoles,
    sanitizeUserPermissions,
    hasPermission,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    getPermissionsForRoles
} from '../utils/rbac-utils';

// Create the authentication context
const AuthContext = createContext<AuthContextType | null>(null);

// Custom hook to use the auth context
export function useAuthContext(): AuthContextType {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }
    return context;
}

interface AuthProviderProps {
    children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {

    // Transform Better-auth user to RBAC user
    const transformUser = useCallback((betterAuthUser: any): RBACUser | null => {
        if (!betterAuthUser) return null;

        try {
            // Parse roles and permissions from string fields (if they exist)
            let userRoles: RoleName[] = [];
            let userPermissions: Permission[] = [];

            // Handle roles - could be string, array, or JSON string
            if (betterAuthUser.roles) {
                if (typeof betterAuthUser.roles === 'string') {
                    try {
                        userRoles = sanitizeUserRoles(JSON.parse(betterAuthUser.roles));
                    } catch {
                        // If not JSON, treat as single role
                        userRoles = sanitizeUserRoles([betterAuthUser.roles]);
                    }
                } else if (Array.isArray(betterAuthUser.roles)) {
                    userRoles = sanitizeUserRoles(betterAuthUser.roles);
                }
            }

            // If no roles are set, assign default 'user' role
            if (userRoles.length === 0) {
                userRoles = ['user'];
            }

            // Handle permissions - could be string, array, or JSON string
            if (betterAuthUser.permissions) {
                if (typeof betterAuthUser.permissions === 'string') {
                    try {
                        userPermissions = sanitizeUserPermissions(JSON.parse(betterAuthUser.permissions));
                    } catch {
                        // If not JSON, ignore
                        userPermissions = [];
                    }
                } else if (Array.isArray(betterAuthUser.permissions)) {
                    userPermissions = sanitizeUserPermissions(betterAuthUser.permissions);
                }
            }

            // If no explicit permissions, derive from roles
            if (userPermissions.length === 0) {
                userPermissions = getPermissionsForRoles(userRoles);
            }



            return {
                id: betterAuthUser.id,
                email: betterAuthUser.email,
                fullName: betterAuthUser.fullName || `${betterAuthUser.firstName} ${betterAuthUser.lastName}`,
                firstName: betterAuthUser.firstName,
                lastName: betterAuthUser.lastName,
                middleName: betterAuthUser.middleName,
                suffix: betterAuthUser.suffix,
                image: betterAuthUser.image,
                phone: betterAuthUser.phone,
                customFields: betterAuthUser.customFields,
                isActive: betterAuthUser.isActive ?? true,
                cartId: betterAuthUser.cartId,
                defaultConversationId: betterAuthUser.defaultConversationId,
                username: betterAuthUser.username,
                displayUsername: betterAuthUser.displayUsername,
                roles: userRoles,
                permissions: userPermissions,
                createdAt: betterAuthUser.createdAt ? new Date(betterAuthUser.createdAt) : undefined,
                updatedAt: betterAuthUser.updatedAt ? new Date(betterAuthUser.updatedAt) : undefined,
            };
        } catch (error) {
            console.error('Error transforming user:', error);
            return null;
        }
    }, []);

    // Transform Better-auth session to RBAC session
    const transformSession = useCallback((betterAuthSession: any, rbacUser: RBACUser): RBACSession | null => {
        if (!betterAuthSession || !rbacUser) return null;

        try {
            // Prefer roles/permissions coming from the session augmentation
            let roles: RoleName[] = Array.isArray(betterAuthSession?.roles)
                ? sanitizeUserRoles(betterAuthSession.roles)
                : [];

            let permissions: Permission[] = Array.isArray(betterAuthSession?.permissions)
                ? sanitizeUserPermissions(betterAuthSession.permissions)
                : [];

            // Fallbacks to RBAC user
            if (roles.length === 0) roles = rbacUser.roles?.length ? rbacUser.roles : ['user'];
            if (permissions.length === 0) permissions = rbacUser.permissions?.length
                ? rbacUser.permissions
                : getPermissionsForRoles(roles);

            return {
                user: rbacUser,
                roles,
                permissions,
                expiresAt: new Date(betterAuthSession.expiresAt),
                token: betterAuthSession.token || '',
            };
        } catch (error) {
            console.error('Error transforming session:', error);
            return null;
        }
    }, []);

    // Use better-auth's useSession hook
    const { data: sessionData, isPending, error } = auth.useSession();

    console.log('Auth Context - sessionData:', sessionData);
    console.log('Auth Context - isPending:', isPending);
    console.log('Auth Context - error:', error);

    // Transform Better-auth user to RBAC user
    const user = useMemo(() => {
        if (!sessionData?.user) return null;
        return transformUser(sessionData.user);
    }, [sessionData?.user, transformUser]);

    // Transform session data
    const session = useMemo(() => {
        if (!sessionData?.session || !user) {
            console.log('Auth Context - No session or user:', {
                hasSession: !!sessionData?.session,
                hasUser: !!user,
                sessionData: sessionData?.session
            });
            return null;
        }
        // Pure computation only; no side-effects here.
        return transformSession(sessionData.session, user);
    }, [sessionData?.session, user, transformSession]);

    // Persist the token to sessionStorage as a side-effect, not during render
    useEffect(() => {
        const token = sessionData?.session?.token;
        if (token) {
            const prev = sessionStorage.getItem('TOKEN');
            if (prev !== token) {
                sessionStorage.setItem('TOKEN', token);
            }
        } else {
            // If there's no token, ensure it's cleared
            sessionStorage.removeItem('TOKEN');
        }
    }, [sessionData?.session?.token]);

    // Derived state
    const roles = useMemo<RoleName[]>(() => {
        // Prefer roles from session augmentation (Better Auth session may be augmented)
        const rawSession: any = sessionData?.session as any;
        const sessionRoles: RoleName[] = Array.isArray(rawSession?.roles)
            ? sanitizeUserRoles(rawSession.roles as string[])
            : ([] as RoleName[]);
        if (sessionRoles.length) return sessionRoles;
        const userRoles: RoleName[] = user?.roles ?? ([] as RoleName[]);
        return userRoles.length ? userRoles : (['user'] as RoleName[]);
    }, [sessionData?.session, user?.roles]);

    const permissions = useMemo<Permission[]>(() => {
        // Prefer permissions from session augmentation (Better Auth session may be augmented)
        const rawSession: any = sessionData?.session as any;
        const sessionPermissions: Permission[] = Array.isArray(rawSession?.permissions)
            ? sanitizeUserPermissions(rawSession.permissions as any[])
            : ([] as Permission[]);
        if (sessionPermissions.length) return sessionPermissions;
        const userPermissions: Permission[] = user?.permissions ?? ([] as Permission[]);
        if (userPermissions.length) return userPermissions;
        // Derive from roles if nothing explicit present
        return getPermissionsForRoles(roles as RoleName[]);
    }, [sessionData?.session, user?.permissions, roles]);
    const isAuthenticated = useMemo(() => !!user && !!session, [user, session]);
    const isLoading = isPending;

    // Refresh session - better-auth handles this automatically
    const refreshSession = useCallback(async () => {
        // The useSession hook automatically handles session refresh
        // This is kept for compatibility with existing code
        console.log('Session refresh requested - handled automatically by better-auth');
    }, []);

    // Sign in function
    const signIn = useCallback(async () => {
        // Better-auth handles the actual sign-in process
        // This is kept for compatibility with existing code
        console.log('Sign in requested - use auth.signIn methods directly');
    }, []);

    // Sign out function
    const signOut = useCallback(async () => {
        try {
            sessionStorage.removeItem("TOKEN")
            await auth.signOut();

            // Session state will be automatically updated by useSession hook
        } catch (error) {
            console.error('Error signing out:', error);
        }
    }, []);

    // Permission checking functions
    const checkPermission = useCallback((resource: string, action: string) => {
        return hasPermission(permissions, resource as any, action as any);
    }, [permissions]);

    const checkRole = useCallback((role: RoleName) => {
        return hasRole(roles, role);
    }, [roles]);

    const checkAnyRole = useCallback((rolesToCheck: RoleName[]) => {
        return hasAnyRole(roles, rolesToCheck);
    }, [roles]);

    const checkAllRoles = useCallback((rolesToCheck: RoleName[]) => {
        return hasAllRoles(roles, rolesToCheck);
    }, [roles]);



    // Context value
    const contextValue: AuthContextType = useMemo(() => ({
        user,
        session,
        roles,
        permissions,
        isLoading,
        isAuthenticated,
        hasPermission: checkPermission,
        hasRole: checkRole,
        hasAnyRole: checkAnyRole,
        hasAllRoles: checkAllRoles,
        signIn,
        signOut,
        refreshSession,
    }), [
        user,
        session,
        roles,
        permissions,
        isLoading,
        isAuthenticated,
        checkPermission,
        checkRole,
        checkAnyRole,
        checkAllRoles,
        signIn,
        signOut,
        refreshSession,
    ]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
}